import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import polylabel from '@mapbox/polylabel';
import * as polygonClipping from 'polygon-clipping';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { useGoogleMapsApi } from '../../../../../GoogleMapsApiContext';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTranslation } from 'react-i18next';
import { LocationArrowIcon } from '../../AddMyPlaceFlow/icons';
import { resolveListingBuilding } from '../../AddMyPlaceFlow/resolveListingBuilding';
import {
  AREA_OPENING_ZOOM,
  areaIdKey,
  fetchAreasInView,
  Bbox,
} from '../neighbourhoodApi';
import { pointInGeometry } from '../../../../utils/geo';
import { placesSearchOptions } from '../../../../utils/placesBias';
import {
  AreaGeometry,
  AreaLevel,
  CouldBuySeed,
  InViewArea,
  NamedArea,
} from '../types';
import EdgeFadeScroller from '../../../../common/EdgeFadeScroller/EdgeFadeScroller';
import {
  BuildingChipIcon,
  ChipRemoveIcon,
  DistrictChipIcon,
  PinChipIcon,
} from '../locationIcons';
import { LatLng } from '../../../../utils/types';
import './MapPicker.scss';

interface MapPickerStepProps {
  seed: CouldBuySeed;
  // Live centre of the main map, read when the picker mounts (JAA-341). Used
  // only when the seed carries no building coordinates, so a flow started from
  // the Activity FAB opens the picker on the area the user was just viewing.
  // Null when the caller deliberately keeps the legacy Helsinki default.
  fallbackCenter: LatLng | null;
  // Districts already chosen on the form, so the picker opens with them selected.
  selectedAreas: NamedArea[];
  // Whether the 500 m circle around the seed building is currently selected.
  circleSelected: boolean;
  // Whether the seed/anchor building itself is selected (mirrors the form's chip).
  anchorSelected: boolean;
  // Commit the picks back to the form: chosen districts, the 500 m circle, the
  // anchor-building selection, and (when the user tapped a different building on
  // the map) the swapped-in anchor building. Closes the picker.
  onDone: (
    areas: NamedArea[],
    circleSelected: boolean,
    anchorSelected: boolean,
    anchor: CouldBuySeed | null,
  ) => void;
}

// Area layer shows up to this zoom; above it the user picks individual buildings
// (the composite building layer renders ~z15+). Raised to 16 so sub-areas stay
// visible right up to building level — no dead-zone where neither shows.
const AREA_LAYER_MAX_ZOOM = 16;
// Area-layer zoom bands (coarse -> fine): city (kunta) up to CITY_LEVEL_MAX_ZOOM,
// district (kaupunginosa) up to DISTRICT_LEVEL_MAX_ZOOM, then sub-area (osa-alue,
// e.g. Kalasatama inside the Sörnäinen district) up to building-pick. City is a
// narrow overview band; district + sub-area are the actionable "could buy" levels.
const CITY_LEVEL_MAX_ZOOM = 9;
const DISTRICT_LEVEL_MAX_ZOOM = 13;
// Floor the zoom at the metro-region scale: any further out the viewport spans
// whole countries, and the area layer would try to grid-sample every city in
// Europe. z8 ≈ the surrounding region, which is as coarse as "could buy" needs.
const PICKER_MIN_ZOOM = 8;
// Open at district scale so several selectable districts are in view right away
// — a more useful starting point for a buyer than individual buildings. z12 sits
// inside the district band (levelForZoom), so the first area fetch resolves
// districts. The anchor footprint resolves lazily once the user zooms into
// building range (the composite/building layer only renders ~z15+).
const INITIAL_DISTRICT_ZOOM = AREA_OPENING_ZOOM;
const AREA_FETCH_DEBOUNCE_MS = 300;
// An incomplete in-view answer is asked for again this often while the camera holds
// still, at most this many times in a row (a failing backend must not be polled forever).
const INCOMPLETE_RETRY_MS = 1500;
const MAX_INCOMPLETE_RETRIES = 6;

// Stable selection key for an in-view/named area (ext_id, else lowercased name).
const areaKey = (area: { ext_id: string; name: string }) =>
  area.ext_id ? areaIdKey(area.ext_id) : area.name.toLowerCase();

// The area level shown at a given zoom (coarse -> fine): city, then district, then
// sub-area (osa-alue, e.g. Kalasatama) once zoomed in past the district band.
const levelForZoom = (zoom: number): AreaLevel => {
  if (zoom <= CITY_LEVEL_MAX_ZOOM) return 'city';
  if (zoom <= DISTRICT_LEVEL_MAX_ZOOM) return 'district';
  return 'sub-area';
};

// Strip an InViewArea down to the NamedArea POST contract (drop picker-only
// geometry/bbox) before handing picks back to the form.
const toNamedArea = (area: InViewArea): NamedArea => ({
  kind: 'area',
  ext_id: area.ext_id,
  name: area.name,
  level: area.level,
});

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

// A good interior point for an area's label — the pole of inaccessibility of its
// largest polygon — so labels sit inside the shape, not at a bbox centre that can
// land in the sea for island/multi-part districts. Falls back to the bbox centre.
const areaLabelPoint = (
  geometry: AreaGeometry,
  areaBbox: Bbox,
  viewBbox: Bbox,
): [number, number] => {
  const pickLargest = (polys: number[][][][]) =>
    polys.reduce((a, b) =>
      (a[0]?.length ?? 0) >= (b[0]?.length ?? 0) ? a : b,
    );
  try {
    const polygons =
      geometry.type === 'Polygon'
        ? [geometry.coordinates as number[][][]]
        : (geometry.coordinates as number[][][][]);
    const [vMinLng, vMinLat, vMaxLng, vMaxLat] = viewBbox;
    const clipRect = [
      [
        [vMinLng, vMinLat],
        [vMaxLng, vMinLat],
        [vMaxLng, vMaxLat],
        [vMinLng, vMaxLat],
        [vMinLng, vMinLat],
      ],
    ];
    // Label the part of the area inside the viewport, so a big district (suurpiiri)
    // gets a label in view rather than at a far-off interior point.
    const clipped = (
      polygonClipping as unknown as {
        intersection: (a: unknown, b: unknown) => number[][][][];
      }
    ).intersection(polygons, clipRect);
    const target =
      clipped && clipped.length ? pickLargest(clipped) : pickLargest(polygons);
    const point = polylabel(target, 0.001);
    return [point[0], point[1]];
  } catch {
    const [minLng, minLat, maxLng, maxLat] = areaBbox;
    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }
};

// Two screen rects overlap (gap = extra spacing required between pills before they
// count as colliding) — used to hide colliding labels. gap 0 = pills may touch; only
// real overlap hides one, so labels pack as tight as possible.
const labelsOverlap = (a: DOMRect, b: DOMRect, gap = 0): boolean =>
  a.left < b.right + gap &&
  a.right + gap > b.left &&
  a.top < b.bottom + gap &&
  a.bottom + gap > b.top;

// Metres each district edge is offset inward so neighbours render with a small,
// UNIFORM gap instead of a shared hard border (gap between two = 2x this).
const AREA_INSET_M = 12;
const M_PER_DEG = 111320;

// Offset a closed ring inward by `meters` (uniform), in a local metric space so
// the inset is even N-S and E-W. Each vertex moves along the bisector of its two
// edges' inward normals, with a miter CAP so near-collinear vertices can't shoot
// off into spikes (intersecting near-parallel offset edges was flinging vertices
// kilometres away on simplified boundaries — the huge/uneven city-zoom gaps).
const offsetRingInward = (ring: number[][], meters: number): number[][] => {
  const closed =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1];
  const pts = closed ? ring.slice(0, -1) : ring.slice();
  const n = pts.length;
  if (n < 3) return ring;
  const cosLat =
    Math.cos((pts.reduce((s, p) => s + p[1], 0) / n) * (Math.PI / 180)) || 1;
  const m = pts.map(([lng, lat]) => [
    lng * M_PER_DEG * cosLat,
    lat * M_PER_DEG,
  ]);
  let area = 0;
  for (let i = 0; i < n; i++) {
    const a = m[i];
    const b = m[(i + 1) % n];
    area += a[0] * b[1] - b[0] * a[1];
  }
  const inward = area > 0 ? 1 : -1;
  const normals = m.map((a, i) => {
    const b = m[(i + 1) % n];
    const nx = -(b[1] - a[1]);
    const ny = b[0] - a[0];
    const len = Math.hypot(nx, ny) || 1;
    return [(nx / len) * inward, (ny / len) * inward];
  });
  const MITER_CAP = 2;
  const out: number[][] = [];
  for (let i = 0; i < n; i++) {
    const e0 = normals[(i - 1 + n) % n];
    const e1 = normals[i];
    let bx = e0[0] + e1[0];
    let by = e0[1] + e1[1];
    const blen = Math.hypot(bx, by);
    if (blen < 1e-9) {
      bx = e1[0];
      by = e1[1];
    } else {
      bx /= blen;
      by /= blen;
    }
    // meters / cos(halfAngle) keeps the perpendicular inset = meters; the cap
    // bounds it so sharp / near-straight corners can't produce long spikes.
    const cosHalf = Math.max(bx * e1[0] + by * e1[1], 1e-3);
    const scale = Math.min(1 / cosHalf, MITER_CAP) * meters;
    out.push([
      (m[i][0] + bx * scale) / (M_PER_DEG * cosLat),
      (m[i][1] + by * scale) / M_PER_DEG,
    ]);
  }
  out.push(out[0]);
  return out;
};

// Inset a Polygon/MultiPolygon for the gap-between-districts look. Labels keep
// the original geometry, so only the drawn shape shrinks.
const insetGeometry = (geometry: AreaGeometry): AreaGeometry =>
  geometry.type === 'Polygon'
    ? {
        type: 'Polygon',
        coordinates: (geometry.coordinates as number[][][]).map((r) =>
          offsetRingInward(r, AREA_INSET_M),
        ),
      }
    : {
        type: 'MultiPolygon',
        coordinates: (geometry.coordinates as number[][][][]).map((poly) =>
          poly.map((r) => offsetRingInward(r, AREA_INSET_M)),
        ),
      };

// Signed area of a closed ring (shoelace); its sign encodes the winding direction.
const ringSignedArea = (ring: number[][]): number => {
  let s = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    s += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return s;
};

// Normalise ring winding (exterior CCW, holes CW) so the inner-band line layer's
// line-offset always pushes INWARD. Area geometry arrives with mixed winding
// (Geoapify polygons vs shapely-difference district remnants), which would otherwise
// send the band outside the outline for half the areas.
const orientPolygon = (rings: number[][][]): number[][][] =>
  rings.map((ring, i) => {
    const isCcw = ringSignedArea(ring) > 0;
    const wantCcw = i === 0;
    return isCcw === wantCcw ? ring : ring.slice().reverse();
  });

const orientRings = (geometry: AreaGeometry): AreaGeometry =>
  geometry.type === 'Polygon'
    ? {
        type: 'Polygon',
        coordinates: orientPolygon(geometry.coordinates as number[][][]),
      }
    : {
        type: 'MultiPolygon',
        coordinates: (geometry.coordinates as number[][][][]).map(
          orientPolygon,
        ),
      };

// Inline SVG for an area-label pill's leading icon. Unselected tags carry a "+"
// add affordance (secondary stroke on the white pill); selected tags carry a
// white check confirming the pick — matching the Figma 3729-98651 states.
const AREA_LABEL_PLUS_ICON =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M12 3.5C12.5523 3.5 13 3.94772 13 4.5V11H19.5C20.0523 11 20.5 11.4477 20.5 12C20.5 12.5523 20.0523 13 19.5 13H13V19.5C13 20.0523 12.5523 20.5 12 20.5C11.4477 20.5 11 20.0523 11 19.5V13H4.5C3.94772 13 3.5 12.5523 3.5 12C3.5 11.4477 3.94772 11 4.5 11H11V4.5C11 3.94772 11.4477 3.5 12 3.5Z" fill="#5b6970"/>' +
  '</svg>';
const AREA_LABEL_CHECK_ICON =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M19.9268 3.35938C20.2806 2.76665 21.0479 2.57294 21.6406 2.92676C22.2334 3.28062 22.4271 4.04788 22.0733 4.64063L12.4492 20.7617C11.8171 21.8201 10.3173 21.9104 9.56252 20.9355L4.01174 13.7656C3.5892 13.2198 3.68879 12.4344 4.23439 12.0117C4.78017 11.5892 5.56562 11.6888 5.9883 12.2344L10.8662 18.5352L19.9268 3.35938Z" fill="#fff"/>' +
  '</svg>';

// A ~500 m geodesic circle polygon around a point — the "within 500 m" area.
const M_PER_DEG_LAT = 111320;
const CIRCLE_RADIUS_M = 500;
const circle500 = (lng: number, lat: number): GeoJSON.Geometry => {
  const r = CIRCLE_RADIUS_M;
  const steps = 64;
  const dLat = r / M_PER_DEG_LAT;
  const dLng = r / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  const ring: number[][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const a = (i / steps) * 2 * Math.PI;
    ring.push([lng + Math.cos(a) * dLng, lat + Math.sin(a) * dLat]);
  }
  return { type: 'Polygon', coordinates: [ring] };
};

// The on-map AREA PICKER (Figma 3647-76763 / 3729-91722 / 3729-98651 /
// 3729-90443). A focused Mapbox map centred on the seed building, opening at
// district zoom with the selectable district layer in view: the user taps a
// district LABEL to select/unselect (multi-select), or zooms in to pick
// individual buildings, and Done returns the picks into the could-buy form.
// District is the minimum map level; city is the only coarser level (street is
// form-only, deferred to phase 2).
const MapPickerStep = ({
  seed,
  fallbackCenter,
  selectedAreas,
  circleSelected,
  anchorSelected,
  onDone,
}: MapPickerStepProps) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // The 500 m circle around the seed building, selected on the map.
  const [circleSel, setCircleSel] = useState(circleSelected);
  // The seed/anchor building itself, selected — mirrors the form's chip.
  const [anchorSel, setAnchorSel] = useState(anchorSelected);
  // Districts/city picks chosen in this picker session, keyed by areaKey.
  const [picks, setPicks] = useState<Map<string, InViewArea>>(new Map());
  // The areas currently rendered in the viewport (the label + polygon layer).
  const [inView, setInView] = useState<InViewArea[]>([]);
  const [fetchingAreas, setFetchingAreas] = useState(false);
  // True once this view's areas could not all be loaded (retries spent or the
  // request failed); the next fetch or camera move clears it.
  const [areasUnavailable, setAreasUnavailable] = useState(false);
  const fetchSeq = useRef(0);
  // Last fetched (whole zoom step, viewport bounds) — drives the skip-when-already-covered
  // check in refreshAreas so a pan within loaded areas doesn't reload them. The backend
  // sizes areas per whole zoom step, so any step change must refetch.
  const lastFetchRef = useRef<{ zoomStep: number; bbox: Bbox } | null>(null);
  // Retries spent on incomplete answers for the current camera; reset when it moves.
  const incompleteRetriesRef = useRef(0);
  // The pending re-run of refreshAreas (an incomplete answer, or a camera that
  // moved mid-fetch), cancelled by any fetch or camera move so a stale one can't
  // supersede the request for the new view.
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Current in-view areas, mirrored to a ref so the once-registered polygon-click
  // handler can resolve a tapped feature back to its area.
  const inViewRef = useRef<InViewArea[]>([]);
  // Auto-reveal budget (JAA-342): while > 0, an app-driven camera placement
  // (open, in-picker search, locate) is allowed to step the zoom one band
  // coarser when the area fetch comes back empty, so towns without sub-area or
  // district rows still show something selectable. Any user-driven camera or
  // map interaction zeroes it — the mechanism never fights the user.
  const autoRevealBudgetRef = useRef(0);
  // Bumped on every user gesture/tap. An async arm (geocode, geolocation)
  // captures it before its await and aborts when it moved — a gesture during
  // the wait means the user took the camera and the pending arm must die.
  const gestureSeqRef = useRef(0);
  // Arm the auto-reveal for one app-driven camera placement. Bumping fetchSeq
  // drops any in-flight result from the previous viewport (and clears the
  // spinner that fetch would otherwise strand), and clearing lastFetchRef
  // forces a fresh fetch after the placement settles — so the reveal decision
  // always runs, even when the destination is inside already-fetched bounds.
  // The old viewport's areas-unavailable notice is dropped with the spinner.
  const armAutoReveal = useCallback((budget: number) => {
    autoRevealBudgetRef.current = budget;
    fetchSeq.current += 1;
    lastFetchRef.current = null;
    setFetchingAreas(false);
    setAreasUnavailable(false);
  }, []);
  const [address, setAddress] = useState('');
  const { isLoaded: mapsApiLoaded } = useGoogleMapsApi();
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  // The anchor building's real footprint, resolved from the vector tiles.
  const [footprint, setFootprint] = useState<GeoJSON.Geometry | null>(null);
  // Mirrored to a ref so the once-registered building-click handler can hit-test a
  // tap against the anchor footprint without re-registering on every resolve.
  const footprintRef = useRef<GeoJSON.Geometry | null>(null);
  useEffect(() => {
    footprintRef.current = footprint;
  }, [footprint]);
  // When the user taps a DIFFERENT building it replaces the anchor (single,
  // swappable): the resolved building (id/address/coords) + its footprint. Null
  // until a swap. Mirrored to a ref for the once-registered click handler.
  const [swapped, setSwapped] = useState<{
    seed: CouldBuySeed;
    footprint: GeoJSON.Geometry | null;
  } | null>(null);
  const swappedRef = useRef<{
    seed: CouldBuySeed;
    footprint: GeoJSON.Geometry | null;
  } | null>(null);
  useEffect(() => {
    swappedRef.current = swapped;
  }, [swapped]);
  // Bumped per swap tap so a slow building lookup that lands after a newer tap
  // is discarded instead of overwriting the anchor the user picked last.
  const swapSeqRef = useRef(0);
  // The effective anchor = the swapped building if changed, else the seed prop.
  const anchorSeed = swapped?.seed ?? seed;
  const anchorFootprint = swapped?.footprint ?? footprint;
  // Marker DOM elements for the in-view area labels, rebuilt whenever inView or
  // the pick set changes; tracked so we can remove them on the next pass.
  const labelMarkersRef = useRef<
    Array<{ marker: mapboxgl.Marker; selected: boolean; bbox: Bbox }>
  >([]);
  const decollideRaf = useRef(false);

  // Greedily hide colliding label pills (collision = real overlap, gap 0, so pills
  // pack tight). Collisions resolve in favour of: selected picks, then areas whose
  // WHOLE polygon is in the viewport, then the one nearest the screen centre. Polygon-
  // in-view + centre distance are dynamic (change as the map pans), recomputed here
  // each frame, not baked into a static priority.
  const decollideLabels = useCallback(() => {
    const items = labelMarkersRef.current;
    if (!items.length) return;
    const map = mapRef.current;
    items.forEach(({ marker }) => {
      marker.getElement().style.visibility = '';
    });
    const vp = map?.getContainer().getBoundingClientRect();
    const cx = vp ? vp.left + vp.width / 2 : 0;
    const cy = vp ? vp.top + vp.height / 2 : 0;
    const b = map?.getBounds();
    const [vw, vs, ve, vn] = b
      ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
      : [-180, -90, 180, 90];
    const ordered = items
      .map((it, i) => {
        const rect = it.marker.getElement().getBoundingClientRect();
        const [x0, y0, x1, y1] = it.bbox;
        const polyFullyIn = x0 >= vw && y0 >= vs && x1 <= ve && y1 <= vn;
        const dist = Math.hypot(
          (rect.left + rect.right) / 2 - cx,
          (rect.top + rect.bottom) / 2 - cy,
        );
        return { i, rect, selected: it.selected, polyFullyIn, dist };
      })
      .sort(
        (a, c) =>
          Number(c.selected) - Number(a.selected) ||
          Number(c.polyFullyIn) - Number(a.polyFullyIn) ||
          a.dist - c.dist,
      );
    const kept: DOMRect[] = [];
    ordered.forEach(({ i, rect }) => {
      if (kept.some((k) => labelsOverlap(k, rect))) {
        items[i].marker.getElement().style.visibility = 'hidden';
      } else {
        kept.push(rect);
      }
    });
  }, []);

  // Coalesce de-collision to one run per frame (markers reposition every frame
  // while the map moves).
  const scheduleDecollideLabels = useCallback(() => {
    if (decollideRaf.current) return;
    decollideRaf.current = true;
    requestAnimationFrame(() => {
      decollideRaf.current = false;
      decollideLabels();
    });
  }, [decollideLabels]);

  // Hydrate the initial pick set from the form's already-chosen districts so the
  // picker opens reflecting prior selections. Geometry is unknown for form picks
  // (they carry no geometry); they re-acquire it once they appear in-view.
  useEffect(() => {
    const initial = new Map<string, InViewArea>();
    selectedAreas.forEach((a) => {
      initial.set(areaKey(a), {
        ...a,
        geometry: { type: 'Polygon', coordinates: [] },
        bbox: [0, 0, 0, 0],
      });
    });
    setPicks(initial);
    // Run once on mount; selectedAreas is the open-time snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle a district/city in the pick set (select/unselect on label tap).
  const toggleArea = useCallback((area: InViewArea) => {
    setPicks((prev) => {
      const next = new Map(prev);
      const key = areaKey(area);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, area);
      }
      return next;
    });
  }, []);

  // Where the picker opens, and so where address predictions are biased
  // (JAA-365): the anchor building, else the main map's current centre.
  // Guard member-wise: the fallback is produced with optional chaining under a
  // non-strict tsconfig, so {lat: undefined} typechecks as LatLng and would
  // crash the Mapbox constructor instead of falling through.
  const pickerCenter =
    seed.coordinates ??
    (fallbackCenter &&
    Number.isFinite(fallbackCenter.lat) &&
    Number.isFinite(fallbackCenter.lng)
      ? fallbackCenter
      : null);

  // Initialise the Mapbox map once at district zoom, centred on that point,
  // else Helsinki, so the selectable district layer is the first thing the
  // buyer sees.
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;
    const center = pickerCenter ?? { lat: 60.1699, lng: 24.9384 };
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!mapboxgl.supported()) return undefined;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      center: [center.lng, center.lat],
      zoom: INITIAL_DISTRICT_ZOOM,
      minZoom: PICKER_MIN_ZOOM,
      attributionControl: false,
    });
    // Compact Mapbox attribution (the ⓘ) bottom-right; the logo is added by the
    // SDK bottom-left. Required attribution, and matches Figma 3729-99723.
    map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      'bottom-right',
    );
    mapRef.current = map;
    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __cbfPickerMap?: mapboxgl.Map }).__cbfPickerMap =
        map;
      (
        window as unknown as { __cbfAutoRevealBudget?: () => number }
      ).__cbfAutoRevealBudget = () => autoRevealBudgetRef.current;
    }

    // Auto-reveal (JAA-342): the opening placement is app-driven, so it may
    // step out to find selectable areas — unless the circle is selected, whose
    // own fit owns the camera. Any user gesture (movestart with a real input
    // event; wheel/drag/double-tap/keyboard all carry one) or any tap on the
    // map hands the camera back to the user for good.
    if (!circleSelected) armAutoReveal(2);
    // Hand the camera back to the user: any gesture or map tap zeroes the
    // budget and invalidates every pending async arm. Only a NEW explicit
    // placement request (another search or locate) may arm again.
    const disarmAutoReveal = () => {
      autoRevealBudgetRef.current = 0;
      gestureSeqRef.current += 1;
    };
    map.on('movestart', (e) => {
      if ((e as { originalEvent?: Event }).originalEvent) disarmAutoReveal();
    });
    map.on('click', disarmAutoReveal);

    map.on('load', () => {
      // The "within 500 m" area circle around the anchor building (under the
      // footprint highlight).
      map.addSource('cbf-circle', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'cbf-circle-fill',
        type: 'fill',
        source: 'cbf-circle',
        paint: { 'fill-color': '#00805a', 'fill-opacity': 0.12 },
      });
      map.addLayer({
        id: 'cbf-circle-line',
        type: 'line',
        source: 'cbf-circle',
        paint: {
          'line-color': '#00996c',
          'line-width': 2,
          'line-dasharray': [2, 1],
        },
      });

      // Selected-building highlight (green, Map UI fill #605dec26 + selected line).
      map.addSource('cbf-selected-building', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'cbf-selected-building-fill',
        type: 'fill',
        source: 'cbf-selected-building',
        paint: { 'fill-color': '#00805a', 'fill-opacity': 0.25 },
      });
      map.addLayer({
        id: 'cbf-selected-building-outline',
        type: 'line',
        source: 'cbf-selected-building',
        paint: { 'line-color': '#00996c', 'line-width': 3 },
      });

      // Selectable area polygons (districts/city); fill uses the Map UI tint.
      map.addSource('cbf-areas', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // Per Figma "Where - District": the SELECTED district shows a green filled
      // polygon + bold outline; unselected districts show only a faint boundary.
      map.addLayer({
        id: 'cbf-areas-fill',
        type: 'fill',
        source: 'cbf-areas',
        paint: {
          // Selected: Figma Fills/Selected/Tertiary #bdeadb99 (pale mint, ~60%).
          // Unselected: a faint green tint so every in-view district reads as a
          // selectable area, not just an outline.
          'fill-color': [
            'case',
            ['boolean', ['get', 'selected'], false],
            '#bdeadb',
            '#00805a',
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['get', 'selected'], false],
            0.6,
            0.12,
          ],
        },
      });
      // Soft light-green INNER band hugging the outline — the "light border" look
      // (not just a hairline). A wide pale-mint line offset INWARD (winding is
      // normalised in orientRings so it never spills outside the outline) with a
      // slight blur for the soft inner fade.
      map.addLayer({
        id: 'cbf-areas-band',
        type: 'line',
        source: 'cbf-areas',
        paint: {
          'line-color': '#00805A',
          'line-width': [
            'case',
            ['boolean', ['get', 'selected'], false],
            12,
            9,
          ],
          'line-offset': [
            'case',
            ['boolean', ['get', 'selected'], false],
            6,
            4.5,
          ],
          'line-blur': 2,
          'line-opacity': 0.1,
        },
      });
      map.addLayer({
        id: 'cbf-areas-outline',
        type: 'line',
        source: 'cbf-areas',
        paint: {
          'line-color': '#00805a',
          'line-width': [
            'case',
            ['boolean', ['get', 'selected'], false],
            2.5,
            1.5,
          ],
          'line-opacity': [
            'case',
            ['boolean', ['get', 'selected'], false],
            1,
            0.65,
          ],
        },
      });

      // Hidden, queryable building footprints — hit-tested to highlight the
      // anchor building's REAL shape (not a placeholder box).
      map.addLayer({
        id: 'cbf-building-hit',
        type: 'fill',
        source: 'composite',
        'source-layer': 'building',
        paint: { 'fill-opacity': 0 },
      });

      map.getCanvas().style.cursor = '';
      map.on('mouseenter', 'building', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'building', () => {
        map.getCanvas().style.cursor = '';
      });

      // Tapping the ANCHOR building footprint toggles its selection (the "I could
      // buy this building" pick), mirrored by the building chip. Taps on any other
      // building are ignored — only the seed building is selectable. The 500 m
      // circle is a separate expansion with its own chip — not toggled here.
      map.on('click', 'building', async (e) => {
        const clickPt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const clicked = e.features?.[0]?.geometry as
          | GeoJSON.Geometry
          | undefined;
        const cur = swappedRef.current;
        const curCoords = cur?.seed.coordinates ?? seed.coordinates;
        const curFootprint = cur?.footprint ?? footprintRef.current;
        // Tapping the CURRENT anchor toggles its selection on/off.
        const onCurrentAnchor =
          !!(
            curCoords &&
            clicked &&
            pointInGeometry([curCoords.lng, curCoords.lat], clicked)
          ) || pointInGeometry(clickPt, curFootprint);
        if (onCurrentAnchor) {
          setAnchorSel((prev) => !prev);
          return;
        }
        // Tapping a DIFFERENT building swaps the anchor to it: resolve its id +
        // address (shared resolver) and highlight its footprint. A lookup that
        // fails leaves the current anchor alone rather than rejecting unhandled.
        const seq = (swapSeqRef.current += 1);
        let resolved: Awaited<ReturnType<typeof resolveListingBuilding>>;
        try {
          resolved = await resolveListingBuilding({
            lat: e.lngLat.lat,
            lng: e.lngLat.lng,
          });
        } catch (err) {
          console.error('Could-buy anchor swap lookup failed', err);
          return;
        }
        if (seq !== swapSeqRef.current) return;
        const addr = resolved.addressObject;
        setSwapped({
          seed: {
            buildingId: resolved.buildingId ?? '',
            coordinates: addr?.coordinates ?? {
              lat: e.lngLat.lat,
              lng: e.lngLat.lng,
            },
            address: {
              street: addr?.street ?? '',
              houseNumber: addr?.houseNumber ?? '',
              city: addr?.city ?? '',
              country: addr?.country ?? '',
            },
          },
          footprint:
            (resolved.geometry as GeoJSON.Geometry | null) ?? clicked ?? null,
        });
        setAnchorSel(true);
      });

      // Tap an area polygon to select it (and reveal its name) — the primary pick
      // gesture at city/district zoom, where labels aren't auto-shown.
      map.on('mouseenter', 'cbf-areas-fill', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'cbf-areas-fill', () => {
        map.getCanvas().style.cursor = '';
      });
      map.on('click', 'cbf-areas-fill', (e) => {
        const key = e.features?.[0]?.properties?.key;
        if (typeof key !== 'string') return;
        const area = inViewRef.current.find((a) => areaKey(a) === key);
        if (area) toggleArea(area);
      });

      setMapReady(true);
    });

    return () => {
      labelMarkersRef.current.forEach((m) => m.marker.remove());
      labelMarkersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // Seed, fallbackCenter and circleSelected are read once at mount — the
    // picker's entry-time snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the anchor building's REAL footprint from the vector tiles once they
  // settle, and cache it (so toggling selection doesn't re-query).
  useEffect(() => {
    const map = mapRef.current;
    const coords = seed.coordinates;
    if (!mapReady || !map || !coords || footprint) return undefined;
    const query = () => {
      if (footprint || !map.getLayer('cbf-building-hit')) return;
      // Query a small box (not a single pixel) around the building point — the
      // centroid can land on a building edge or courtyard and miss a point hit.
      const p = map.project([coords.lng, coords.lat]);
      const r = 8;
      const hit = map.queryRenderedFeatures(
        [
          [p.x - r, p.y - r],
          [p.x + r, p.y + r],
        ],
        { layers: ['cbf-building-hit'] },
      );
      if (hit[0]) setFootprint(hit[0].geometry as GeoJSON.Geometry);
    };
    query();
    map.on('idle', query);
    return () => {
      map.off('idle', query);
    };
  }, [mapReady, seed.coordinates, footprint]);

  // Draw / clear the seed-building highlight (its REAL footprint only — no
  // placeholder square, so nothing wrong-shaped flashes before the footprint
  // resolves) AND the 500 m "within 500 m" area circle around it.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const buildingSrc = map.getSource('cbf-selected-building') as
      | mapboxgl.GeoJSONSource
      | undefined;
    const circleSrc = map.getSource('cbf-circle') as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!buildingSrc) return;
    const coords = anchorSeed.coordinates;
    if (!coords) {
      buildingSrc.setData(EMPTY_FC);
      circleSrc?.setData(EMPTY_FC);
      return;
    }
    // The (effective) anchor building's footprint is highlighted only while it's
    // selected, so tapping visibly selects/unselects it — and a swap moves the
    // highlight to the newly-tapped building.
    buildingSrc.setData(
      anchorSel && anchorFootprint
        ? {
            type: 'FeatureCollection',
            features: [
              { type: 'Feature', properties: {}, geometry: anchorFootprint },
            ],
          }
        : EMPTY_FC,
    );
    // The 500 m circle area only renders when that expansion is selected; it
    // centres on the effective anchor (the swapped building when changed).
    circleSrc?.setData(
      circleSel
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: circle500(coords.lng, coords.lat),
              },
            ],
          }
        : EMPTY_FC,
    );
  }, [anchorSel, circleSel, anchorFootprint, anchorSeed.coordinates, mapReady]);

  // When the 500 m circle is selected IN the picker, frame the whole circle: at
  // building zoom a 1 km-diameter circle overflows the viewport, so fitting its
  // bounds zooms to ~14 where it sits in view. Keyed on select (not anchorSel)
  // so we don't fight the user's panning. The first qualifying run is skipped
  // when the circle arrived pre-selected from the form, so the picker still
  // opens at the district-scale default instead of framing the circle.
  const skipInitialCircleFitRef = useRef(circleSelected);
  useEffect(() => {
    if (!mapReady || !mapRef.current || !circleSel) return;
    if (skipInitialCircleFitRef.current) {
      skipInitialCircleFitRef.current = false;
      return;
    }
    const coords = anchorSeed.coordinates;
    if (!coords) return;
    // The circle's own fit owns the camera from here — the auto-reveal must
    // never zoom away from a circle the user just chose to see (JAA-342).
    autoRevealBudgetRef.current = 0;
    gestureSeqRef.current += 1;
    const dLat = CIRCLE_RADIUS_M / M_PER_DEG_LAT;
    const dLng =
      CIRCLE_RADIUS_M /
      (M_PER_DEG_LAT * Math.cos((coords.lat * Math.PI) / 180));
    mapRef.current.fitBounds(
      [
        [coords.lng - dLng, coords.lat - dLat],
        [coords.lng + dLng, coords.lat + dLat],
      ],
      { padding: 48, duration: 700 },
    );
  }, [circleSel, anchorSeed.coordinates, mapReady]);

  // The 500 m circle is geometrically tied to the anchor building, so
  // unselecting the building — by tapping it on the map or removing its chip —
  // clears the circle too. Keeps `circleSel ⟹ anchorSel`.
  useEffect(() => {
    if (!anchorSel && circleSel) setCircleSel(false);
  }, [anchorSel, circleSel]);

  // Fetch the in-view area layer whenever the viewport settles, but only at the
  // area zoom bands; clear it when zoomed into building-pick range. Skips the fetch
  // when the already-loaded areas still cover the viewport (zoom-in / small pan at
  // the same level), so those gestures don't reload or flash the polygons. When an
  // answer is still incomplete after the retries, or the request fails, the status
  // pill shows the areas-unavailable notice instead of a silently half-empty map;
  // the next fetch clears it.
  const refreshAreas = useCallback(async () => {
    clearTimeout(refreshRetryTimerRef.current);
    setAreasUnavailable(false);
    const map = mapRef.current;
    if (!map) return;
    if (map.getZoom() > AREA_LAYER_MAX_ZOOM) {
      // Bump the seq so any in-flight fetch from a lower zoom can't repopulate
      // the cleared district layer once the user is in building-pick range.
      fetchSeq.current += 1;
      setInView([]);
      setFetchingAreas(false);
      lastFetchRef.current = null;
      return;
    }
    const b = map.getBounds();
    if (!b) return;
    const bbox: Bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const zoom = map.getZoom();
    const level = levelForZoom(zoom);
    const zoomStep = Math.floor(zoom);
    // Same zoom step and the new bounds sit INSIDE the last fetched bounds → the loaded
    // areas already cover this view; keep them, skip the refetch.
    const last = lastFetchRef.current;
    if (
      last &&
      last.zoomStep === zoomStep &&
      last.bbox[0] <= bbox[0] &&
      last.bbox[1] <= bbox[1] &&
      last.bbox[2] >= bbox[2] &&
      last.bbox[3] >= bbox[3]
    ) {
      // Areas already on screen satisfy the auto-reveal goal even though no
      // fresh commit happens on this path — retire the budget here too, so a
      // repeat search over covered ground can't leave it armed indefinitely.
      if (inViewRef.current.length > 0) {
        autoRevealBudgetRef.current = 0;
        return;
      }
      // Covered-but-EMPTY with an armed budget must not strand the reveal:
      // fall through to a fresh fetch so the step-out decision gets to run.
      if (autoRevealBudgetRef.current === 0) return;
    }
    // True once the camera has left the bounds this request was made for.
    const cameraLeft = () => {
      const nowB = map.getBounds();
      return (
        !nowB ||
        Math.abs(nowB.getWest() - bbox[0]) +
          Math.abs(nowB.getSouth() - bbox[1]) +
          Math.abs(nowB.getEast() - bbox[2]) +
          Math.abs(nowB.getNorth() - bbox[3]) >
          1e-6
      );
    };
    // Guard against out-of-order responses: only the latest fetch may commit.
    const seq = (fetchSeq.current += 1);
    setFetchingAreas(true);
    try {
      // The throwing variant: a request failure must land in the catch below,
      // never masquerade as a confirmed-empty area (JAA-342's auto-reveal
      // would otherwise zoom out on a network blip).
      const { areas, complete } = await fetchAreasInView(bbox, level, zoom);
      if (seq !== fetchSeq.current) return;
      // A response whose camera has since changed must not commit: it would
      // cache intermediate bounds that the settled viewport then sits inside,
      // skipping the settled fetch and deciding the reveal on stale rows. The
      // settled camera's own fetch cycle redoes it against the real viewport.
      if (cameraLeft()) {
        // Not every viewport change signals moveend (a container resize while
        // the flow's open transition runs does not), so re-schedule instead of
        // waiting for one; the seq guard retires this chain the moment any
        // newer fetch starts, and it ends itself once the bounds hold still.
        refreshRetryTimerRef.current = setTimeout(
          refreshAreas,
          AREA_FETCH_DEBOUNCE_MS,
        );
        return;
      }
      setInView(areas);
      // Only a complete answer may count as covering this view; an incomplete one is asked
      // for again while the camera holds still, so the rest of the areas load in.
      lastFetchRef.current = complete ? { zoomStep, bbox } : null;
      if (complete) {
        incompleteRetriesRef.current = 0;
      } else if (incompleteRetriesRef.current < MAX_INCOMPLETE_RETRIES) {
        incompleteRetriesRef.current += 1;
        refreshRetryTimerRef.current = setTimeout(
          refreshAreas,
          INCOMPLETE_RETRY_MS,
        );
      } else {
        setAreasUnavailable(true);
      }
      // Auto-reveal (JAA-342), success-commit only — never the catch branch (a
      // network blip is not evidence of an empty area) and never the
      // building-zoom clear above. The whole decision waits for an idle
      // camera: a commit from a mid-animation viewport must neither retire nor
      // spend the budget the arm was issued for — the settle's own fetch
      // (lastFetchRef was cleared at arm) re-decides on the real destination.
      // An incomplete answer is not evidence of an empty area either.
      if (!map.isMoving()) {
        if (areas.length > 0) {
          autoRevealBudgetRef.current = 0;
        } else if (
          complete &&
          autoRevealBudgetRef.current > 0 &&
          level !== 'city'
        ) {
          autoRevealBudgetRef.current -= 1;
          map.easeTo({
            zoom:
              level === 'sub-area'
                ? DISTRICT_LEVEL_MAX_ZOOM - 0.5
                : CITY_LEVEL_MAX_ZOOM - 0.5,
            duration: 600,
          });
        }
      }
    } catch {
      if (seq !== fetchSeq.current) return;
      setInView([]);
      lastFetchRef.current = null;
      // A failure for a view the camera already left must not label the new one.
      if (!cameraLeft()) setAreasUnavailable(true);
    } finally {
      if (seq === fetchSeq.current) {
        setFetchingAreas(false);
      }
    }
  }, []);

  // Load the area layer once the map is ready, and again after every camera move.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined;
    const map = mapRef.current;
    let timer: ReturnType<typeof setTimeout>;
    // On every camera move: drop pending retries and the notice, then fetch the
    // new view once it settles.
    const schedule = () => {
      clearTimeout(timer);
      clearTimeout(refreshRetryTimerRef.current);
      incompleteRetriesRef.current = 0;
      setAreasUnavailable(false);
      timer = setTimeout(refreshAreas, AREA_FETCH_DEBOUNCE_MS);
    };
    map.on('moveend', schedule);
    map.on('zoomend', schedule);
    refreshAreas();
    return () => {
      clearTimeout(timer);
      clearTimeout(refreshRetryTimerRef.current);
      map.off('moveend', schedule);
      map.off('zoomend', schedule);
    };
  }, [mapReady, refreshAreas]);

  // Render the in-view area polygons (with a `selected` flag) and their tappable
  // labels. Labels — not polygons — drive selection, per the Figma flow.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const source = map.getSource('cbf-areas') as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) return;
    const bounds = map.getBounds();
    const viewBbox: Bbox = bounds
      ? [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ]
      : [-180, -90, 180, 90];

    const isSelected = (area: InViewArea) => picks.has(areaKey(area));
    inViewRef.current = inView;

    source.setData({
      type: 'FeatureCollection',
      features: inView.map((area) => ({
        type: 'Feature',
        properties: {
          selected: isSelected(area),
          name: area.name,
          key: areaKey(area),
        },
        geometry: orientRings(insetGeometry(area.geometry)) as GeoJSON.Geometry,
      })),
    });

    labelMarkersRef.current.forEach((m) => m.marker.remove());
    labelMarkersRef.current = inView.map((area) => {
      const selected = isSelected(area);
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `cbf-map__area-label${selected ? ' cbf-map__area-label--selected' : ''}`;
      const icon = document.createElement('span');
      icon.className = 'cbf-map__area-label-icon';
      icon.innerHTML = selected ? AREA_LABEL_CHECK_ICON : AREA_LABEL_PLUS_ICON;
      const text = document.createElement('span');
      text.className = 'cbf-map__area-label-text';
      text.textContent = area.name;
      el.append(icon, text);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleArea(area);
      });
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat(areaLabelPoint(area.geometry, area.bbox, viewBbox))
        .addTo(map);
      return { marker, selected, bbox: area.bbox };
    });
    scheduleDecollideLabels();
  }, [inView, picks, mapReady, toggleArea, scheduleDecollideLabels]);

  // Re-run label de-collision while the map moves (markers reposition each frame).
  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined;
    const map = mapRef.current;
    map.on('move', scheduleDecollideLabels);
    return () => {
      map.off('move', scheduleDecollideLabels);
    };
  }, [mapReady, scheduleDecollideLabels]);

  // Geolocate the device, drop a "you are here" marker, and fly to it.
  const handleLocate = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    const gesture = gestureSeqRef.current;
    navigator.geolocation.getCurrentPosition((pos) => {
      const map = mapRef.current;
      if (!map) return;
      const center: [number, number] = [
        pos.coords.longitude,
        pos.coords.latitude,
      ];
      if (userMarkerRef.current) {
        userMarkerRef.current.setLngLat(center);
      } else {
        const el = document.createElement('div');
        el.className = 'cbf-map__user-dot';
        userMarkerRef.current = new mapboxgl.Marker({ element: el })
          .setLngLat(center)
          .addTo(map);
      }
      // A gesture during the (slow) geolocation wait means the user owns the
      // camera now — drop the whole fly, keep only the informational marker.
      if (gestureSeqRef.current !== gesture) return;
      // One courtesy step at most (JAA-342): abroad or at sea, a second silent
      // zoom-out would destroy the location the user explicitly asked for.
      armAutoReveal(1);
      map.flyTo({ center, zoom: 14, duration: 1200 });
    });
  }, [armAutoReveal]);

  const handleDone = useCallback(() => {
    onDone(
      Array.from(picks.values()).map(toNamedArea),
      circleSel,
      anchorSel,
      swapped?.seed ?? null,
    );
  }, [picks, circleSel, anchorSel, swapped, onDone]);

  // Geocode a chosen Google Places suggestion and frame it: a place's rectangle
  // (bounds/viewport) → fitBounds, so a city frames the city and an address lands
  // at street level; falls back to flyTo for points with no extent.
  const handleSelectAddress = useCallback(
    async (selected: string) => {
      setAddress(selected);
      const gesture = gestureSeqRef.current;
      try {
        const results = await geocodeByAddress(selected);
        const map = mapRef.current;
        if (!map || !results[0]) return;
        const geometry = results[0].geometry as {
          bounds?: google.maps.LatLngBounds;
          viewport?: google.maps.LatLngBounds;
        };
        const rect = geometry.bounds ?? geometry.viewport;
        // A gesture during the geocode wait means the user owns the camera now:
        // drop the stale jump entirely (and its arm), but still clear the box.
        if (gestureSeqRef.current !== gesture) {
          setAddress('');
          return;
        }
        // Arm immediately before each camera call, never before an await that
        // can reject: an armed budget with no app placement behind it could
        // later fire under a user-owned viewport (JAA-342).
        if (rect) {
          armAutoReveal(2);
          map.fitBounds(
            [
              [rect.getSouthWest().lng(), rect.getSouthWest().lat()],
              [rect.getNorthEast().lng(), rect.getNorthEast().lat()],
            ],
            { duration: 1200, maxZoom: 16, padding: 40 },
          );
        } else {
          const { lat, lng } = await getLatLng(results[0]);
          if (gestureSeqRef.current !== gesture) {
            setAddress('');
            return;
          }
          armAutoReveal(2);
          map.flyTo({ center: [lng, lat], zoom: 14, duration: 1200 });
        }
        setAddress('');
      } catch {
        // geocoding failed — leave the box as typed
      }
    },
    [armAutoReveal],
  );

  const anchorLabel = [
    anchorSeed.address.street,
    anchorSeed.address.houseNumber,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className="cbf-step cbf-step--map">
      {/* Header: anchor chip when the building is selected, else the prompt. */}
      <div className="cbf-map__header">
        <h2 className="cbf-map__title">
          {t('newLandingPage.couldBuy.mapPicker.title')}
        </h2>
        {/* Fixed-height row: the selected chips when there's a selection, else the
            prompt — swapping content never changes the header height. */}
        <div className="cbf-map__subrow">
          {anchorSel || circleSel || picks.size > 0 ? (
            <EdgeFadeScroller className="cbf-map__picks">
              {anchorSel && anchorLabel && (
                <button
                  type="button"
                  className="cbf-chip cbf-chip--selected"
                  onClick={() => setAnchorSel(false)}
                  aria-label={t(
                    'newLandingPage.couldBuy.mapPicker.unselectBuildingAria',
                  )}
                >
                  <span className="cbf-chip__icon" aria-hidden="true">
                    <BuildingChipIcon color="#fff" />
                  </span>
                  <span className="cbf-chip__label">{anchorLabel}</span>
                  <span className="cbf-chip__remove" aria-hidden="true">
                    <ChipRemoveIcon />
                  </span>
                </button>
              )}
              {circleSel && (
                <button
                  type="button"
                  className="cbf-chip cbf-chip--selected"
                  onClick={() => setCircleSel(false)}
                  aria-label={t(
                    'newLandingPage.couldBuy.mapPicker.removeAreaAria',
                    {
                      name: t('newLandingPage.couldBuy.main.within500m'),
                    },
                  )}
                >
                  <span className="cbf-chip__icon" aria-hidden="true">
                    <PinChipIcon color="#fff" />
                  </span>
                  <span className="cbf-chip__label">
                    {t('newLandingPage.couldBuy.main.within500m')}
                  </span>
                  <span className="cbf-chip__remove" aria-hidden="true">
                    <ChipRemoveIcon />
                  </span>
                </button>
              )}
              {Array.from(picks.values()).map((area) => (
                <button
                  key={areaKey(area)}
                  type="button"
                  className="cbf-chip cbf-chip--selected"
                  onClick={() => toggleArea(area)}
                  aria-label={t(
                    'newLandingPage.couldBuy.mapPicker.removeAreaAria',
                    { name: area.name },
                  )}
                >
                  <span className="cbf-chip__icon" aria-hidden="true">
                    <DistrictChipIcon color="#fff" />
                  </span>
                  <span className="cbf-chip__label">{area.name}</span>
                  <span className="cbf-chip__remove" aria-hidden="true">
                    <ChipRemoveIcon />
                  </span>
                </button>
              ))}
            </EdgeFadeScroller>
          ) : (
            <p className="cbf-map__subtitle">
              {t('newLandingPage.couldBuy.mapPicker.subtitle')}
            </p>
          )}
        </div>
      </div>

      {/* Map canvas with the search pill overlay and floating Done button. */}
      <div className="cbf-map__canvas-wrap">
        <div ref={mapContainerRef} className="cbf-map__canvas" />

        <div className="cbf-map__search-row">
          {/* Tapping anywhere on the pill focuses the search input — its content is
              centred when resting, so the empty sides would otherwise be dead. */}
          <div
            className="cbf-map__search-pill"
            onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
          >
            <span className="cbf-map__search-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.5 2C15.1944 2 19 5.80558 19 10.5C19 12.3963 18.3778 14.1466 17.3281 15.5605L20.8838 19.1162C21.3719 19.6044 21.3719 20.3956 20.8838 20.8838C20.3956 21.3719 19.6044 21.3719 19.1162 20.8838L15.5605 17.3281C14.1466 18.3778 12.3963 19 10.5 19C5.80558 19 2 15.1944 2 10.5C2 5.80558 5.80558 2 10.5 2ZM10.5 4C6.91015 4 4 6.91015 4 10.5C4 14.0899 6.91015 17 10.5 17C14.0899 17 17 14.0899 17 10.5C17 6.91015 14.0899 4 10.5 4Z"
                  fill="#5b6970"
                />
              </svg>
            </span>
            {mapsApiLoaded ? (
              <PlacesAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={handleSelectAddress}
                searchOptions={placesSearchOptions(pickerCenter)}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <div className="cbf-map__search-ac">
                    <input
                      {...getInputProps({
                        placeholder: t(
                          'newLandingPage.couldBuy.mapPicker.searchPlaceholder',
                        ),
                        className: 'cbf-map__search-input',
                        'aria-label': t(
                          'newLandingPage.couldBuy.mapPicker.searchPlaceholder',
                        ),
                      })}
                    />
                    {(loading || suggestions.length > 0) && (
                      <ul className="cbf-map__search-results">
                        {suggestions.map((s) => (
                          <li
                            key={s.placeId}
                            {...getSuggestionItemProps(s, {
                              className: 'cbf-map__search-result',
                            })}
                          >
                            {s.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </PlacesAutocomplete>
            ) : (
              <input
                className="cbf-map__search-input"
                placeholder={t(
                  'newLandingPage.couldBuy.mapPicker.searchPlaceholder',
                )}
                aria-label={t(
                  'newLandingPage.couldBuy.mapPicker.searchPlaceholder',
                )}
                disabled
              />
            )}
          </div>
          <button
            type="button"
            className="cbf-map__locate-btn"
            onClick={handleLocate}
            aria-label={t('newLandingPage.couldBuy.mapPicker.myLocationAria')}
          >
            <LocationArrowIcon />
          </button>
        </div>

        {/* Custom +/- zoom control (Figma 3729-99723): white pill, bottom-right. */}
        <div
          className="cbf-map__zoom"
          role="group"
          aria-label={t('newLandingPage.couldBuy.mapPicker.zoomAria')}
        >
          <button
            type="button"
            className="cbf-map__zoom-btn"
            aria-label={t('newLandingPage.couldBuy.mapPicker.zoomInAria')}
            onClick={() => {
              autoRevealBudgetRef.current = 0;
              gestureSeqRef.current += 1;
              mapRef.current?.zoomIn();
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7.5 2.6v9.8M2.6 7.5h9.8"
                stroke="#131c20"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className="cbf-map__zoom-divider" aria-hidden="true" />
          <button
            type="button"
            className="cbf-map__zoom-btn"
            aria-label={t('newLandingPage.couldBuy.mapPicker.zoomOutAria')}
            onClick={() => {
              autoRevealBudgetRef.current = 0;
              gestureSeqRef.current += 1;
              mapRef.current?.zoomOut();
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2.6 7.5h9.8"
                stroke="#131c20"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {(fetchingAreas || areasUnavailable) && (
          <div className="cbf-map__loading" role="status" aria-live="polite">
            {fetchingAreas ? (
              <>
                <span className="cbf-map__loading-spinner" aria-hidden="true" />
                {t('newLandingPage.couldBuy.mapPicker.loadingAreas')}
              </>
            ) : (
              t('newLandingPage.couldBuy.mapPicker.areasUnavailable')
            )}
          </div>
        )}

        <div className="cbf-map__done-wrap">
          <button
            type="button"
            className="cbf-map__done-btn"
            onClick={handleDone}
          >
            {t('newLandingPage.couldBuy.mapPicker.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapPickerStep;
